import axios, { isAxiosError } from "axios";
import { getRailsClient, getDirectoryClient } from "../client";
import { buildHeaderForUpload, getHeaders } from "../utils/headers";
import { isGuestAccessMode } from "../utils/guest";
import type { UserProfile } from "../client";

export interface UserRailsProfile {
  currentLevel: CurrentLevel;
  totalTime: number;
  lastMonthTotalTime: number;
  lastMonthPoints: number;
  totalPoints: number;
  gravatarId: string;
  smallImageUrl: string | null;
  thumbImageUrl: string | null;
  hasAccessToDojoActivities: boolean; // this is access to champions passport
}

// The `user` object returned by /api/internal_api/v1/current_user/show with
// filter[]=user (Rails UserPresenters::Show), camelized by the rails client
export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  company: string | null;
  jobRole: string | null;
  country: string | null;
  statusEmail: boolean;
  consent: boolean;
  currentRank: string | null;
  trial: boolean;
  locale: string | null;
  videoSettings: Record<string, unknown>;
  listViewPreferences: Record<string, unknown>;
  learnPanelWidth: number;
  certFirstName: string | null;
  certLastName: string | null;
  breakFixFeatureTourCompleted: boolean;
  gravatarId: string;
  smallImageUrl: string | null;
  thumbImageUrl: string | null;
  modulesConsumed: number;
  rating: number;
  ranking: number;
  totalPoints: number;
  lastMonthPoints: number;
  journeymapType: string | null;
  journeymapScaleIndex: number | null;
  interfaceType: string | null;
  moduleViewMode: string | null;
  latestChangesLastDateViewed: string | null;
  canSelectRoles: boolean;
  hasAdminAccess: boolean;
  hasAccessToDojoActivities: boolean;
  isAssessmentMessageShown: boolean;
  isDojoPreviewShown: boolean;
  isParticipantOfLeaderboard: boolean;
  isParticipantOfAchievementWall: boolean;
  isFinishedVideoModalShown: boolean;
  newFeatureTourCompleted: boolean;
  signupSurveyCompleted: boolean;
  isPending: boolean;
  recordsPerPage: number;
}

interface CurrentLevel {
  id: number;
  levelName: string;
  statsCurrent: number;
  statsTotal: number;
  statsProgress: number;
  pathName: string;
}

const LICENSE_CHECK_RETRY_DELAY_MS = 500;

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const requestUserLicenseStatus = async (tenant_name: string): Promise<boolean> => {
  const API = `/api/check_license`;
  const { data } = await getRailsClient().post(API, { tenant_name });
  return data.licensed;
};

export const checkUserLicenseStatus = async (tenant_name: string): Promise<boolean> => {
  try {
    return await requestUserLicenseStatus(tenant_name);
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 403) {
      console.error("Error checking user license status:", error);
      return false;
    }
  }

  // JIT provisioning can briefly 403 before the user record is available.
  console.warn(`Received 403 when checking license for tenant "${tenant_name}", retrying...`);
  await wait(LICENSE_CHECK_RETRY_DELAY_MS);

  try {
    return await requestUserLicenseStatus(tenant_name);
  } catch (retryError) {
    console.error("Error checking user license status after retry:", retryError);
    return false;
  }
};

export const fetchUserParticipationSettings = async () => {
  try {
    const API = `/api/internal_api/atlas/v1/current_user/participation`;
    const { data } = await getRailsClient().get(API);
    return data;
  } catch (error) {
    console.error("Error fetching user settings:", error);
  }
};

export const updateUserAttributes = async (name: string, value: unknown) => {
  const API = `/api/internal_api/v1/current_user/update`;

  try {
    const payload = { user: { [name]: value } };
    const response = await getRailsClient().put(API, payload);
    return response?.data?.user;
  } catch (error) {
    console.error("Error updating user attributes:", error);
    throw error;
  }
};

export const updateNameOnCertificate = async (certFirstName: string, certLastName: string) => {
  const API = `/api/internal_api/v1/current_user/update`;

  try {
    const payload = {
      user: {
        certFirstName,
        certLastName,
      },
      merge: false,
    };
    const response = await getRailsClient().put(API, payload);
    return response?.data?.user;
  } catch (error) {
    console.error("Error updating certificate name:", error);
    throw error;
  }
};

// Raw (snake_case) user returned by the image-upload endpoint. Unlike most
// calls this uses axios directly, so the response is NOT camelized.
interface UploadedUserImageResponse {
  small_image_url: string | null;
  thumb_image_url: string | null;
  [key: string]: unknown;
}

export const uploadUserImage = async (file: File): Promise<UploadedUserImageResponse> => {
  const API = "/api/internal_api/v1/current_user/update";

  try {
    const formData = new FormData();
    formData.append("user[image]", file);

    const response = await axios.put<{ user: UploadedUserImageResponse }>(API, formData, {
      headers: await buildHeaderForUpload(),
    });

    return response?.data?.user;
  } catch (error) {
    console.error("Error uploading user image:", error);
    throw error;
  }
};

export const fetchUserPathData = async () => {
  try {
    const API = `/api/internal_api/v1/current_user/show?filter[]=passed_belts&filter[]=user`;
    const { data } = await getRailsClient().get(API);

    const sortedPassedBelts = [...(data.passedBelts || [])].sort((a, b) => {
      const dateA = new Date(a.completedAt).getTime();
      const dateB = new Date(b.completedAt).getTime();

      // Push invalid dates to the end
      if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
      if (Number.isNaN(dateA)) return 1;
      if (Number.isNaN(dateB)) return -1;

      // Newest first
      return dateB - dateA;
    });

    return {
      user: data.user,
      passedBelts: sortedPassedBelts,
    };
  } catch (error) {
    console.error("Error fetching user path data:", error);
    throw error;
  }
};

export const downloadPathCertificate = async (roleId: number, userId?: number) => {
  const API = `/api/internal_api/atlas/v1/roles/${roleId}/certificates`;
  const headers = await getHeaders();
  // Include userId only when called in an admin context
  // otherwise backend will default to `current_user`
  const params = userId != null ? { userId } : undefined;
  const response = await axios.get(API, {
    responseType: "blob",
    headers,
    params,
  });

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  try {
    link.href = url;
    link.setAttribute("download", `certificate-${roleId}.pdf`);
    document.body.appendChild(link);
    link.click();
  } finally {
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export const fetchUserPreferences = async () => {
  try {
    const API = `/api/internal_api/v1/current_user/show?filter[]=admin`;
    const { data } = await getRailsClient().get(API);
    return data?.user || {};
  } catch (error) {
    console.error("Error fetching user preferences:", error);
    throw error;
  }
};

export const updateRecordsPerPage = async (recordsPerPage: number) => {
  const API = `/api/internal_api/v1/current_user/update`;
  try {
    const { data } = await getRailsClient().put(API, {
      user: { recordsPerPage },
    });
    return data?.user || {};
  } catch (error) {
    console.error("Error updating records per page:", error);
    throw error;
  }
};

export const fetchUserProfileData = async (): Promise<UserRailsProfile> => {
  try {
    const API = "/api/internal_api/atlas/v1/current_user/profile";
    const { data } = await getRailsClient().get(API);
    if (!data) {
      throw new Error("No user profile data found");
    }
    return data as UserRailsProfile;
  } catch (error) {
    console.error("Error fetching user stats:", error);
    throw error;
  }
};

export const fetchProfileAchievements = async () => {
  try {
    const ACHIEVEMENTS_API = "/api/internal_api/atlas/v1/current_user/achievements";
    const { data } = await getRailsClient().get(ACHIEVEMENTS_API);
    return data;
  } catch (error) {
    console.error("Error fetching profile achievements:", error);
  }
};

export const fetchUserVideoSettings = async () => {
  // Don't try to retrieve video settings in guest access mode
  if (isGuestAccessMode()) {
    return null;
  }

  try {
    const API = "/api/internal_api/v1/current_user/show?filter[]=user";
    const {
      data: {
        user: { videoSettings },
      },
    } = await getRailsClient().get(API);
    return videoSettings;
  } catch (error) {
    console.error("Error fetching user video settings:", error);
  }
};

export const updateUser = async (userProfile: Partial<UserProfile>) => {
  try {
    const client = getDirectoryClient();
    const { data, error } = await client.updateSelf(userProfile);

    if (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export const getPathByCollectionKey = async (collectionKey: string) => {
  try {
    const API = `/api/internal_api/atlas/v1/roles/${collectionKey}`;
    const { data } = await getRailsClient().get(API);
    return data;
  } catch (error) {
    console.error("Error fetching path by collection key:", error);
  }
};
